import { Bell, Send } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useNotificationChannels, useSendTestNotification } from '../hooks/useNotifications.js';
import { ChannelSection } from './NotificationChannelSection.js';
import { RuleSection } from './NotificationRuleSection.js';

/** @graph-connects none */
const STATUS_OPTIONS = [
  { value: 'success' as const, labelKey: 'notification.status.success' },
  { value: 'warning' as const, labelKey: 'notification.status.warning' },
  { value: 'error' as const, labelKey: 'notification.status.error' },
];

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business テスト通知送信セクション。タイトル・項目・ステータスを入力してテストチャンネルに送信する。
 * @graph-connects useSendTestNotification [calls] テスト通知送信
 */
function TestNotificationSection() {
  const { t } = useTranslation();
  const [title, setTitle] = useState(() => t('notification.testDefaultTitle'));
  const [items, setItems] = useState(() => t('notification.testDefaultItems'));
  const [status, setStatus] = useState<'success' | 'warning' | 'error'>('success');
  const mutation = useSendTestNotification();

  return (
    <Card className="p-5">
      <h2 className="text-sm font-bold text-[var(--color-text-primary)]">
        {t('notification.testTitle')}
      </h2>
      <p className="mt-1 text-xs text-[var(--color-text-muted)]">
        {t('notification.testDescription')}
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block text-xs text-[var(--color-text-secondary)]">
          {t('notification.titleLabel')}
          <input
            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs text-[var(--color-text-primary)]"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
          />
        </label>
        <label className="block text-xs text-[var(--color-text-secondary)]">
          {t('common.status')}
          <select
            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs text-[var(--color-text-primary)]"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as 'success' | 'warning' | 'error');
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="mt-3 block text-xs text-[var(--color-text-secondary)]">
        {t('notification.itemsLabel')}
        <textarea
          className="mt-1 h-20 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3 text-xs text-[var(--color-text-primary)]"
          value={items}
          onChange={(e) => {
            setItems(e.target.value);
          }}
        />
      </label>
      <div className="mt-3 flex items-center gap-3">
        <Button
          variant="primary"
          disabled={mutation.isPending || !title || !items.trim()}
          onClick={() => {
            const itemList = items
              .split('\n')
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
            mutation.mutate({ title, items: itemList, status });
          }}
        >
          <Send className="mr-1.5 h-4 w-4" aria-hidden="true" />
          {t('notification.send')}
        </Button>
        {mutation.isSuccess ? (
          <span className="text-xs text-[var(--color-accent-primary)]">
            {t('notification.sendSuccess', { channel: mutation.data.channel })}
          </span>
        ) : null}
        {mutation.isError ? (
          <span className="text-xs text-[var(--color-error)]">{t('notification.sendError')}</span>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business PORTER 通知設定ページ。Slack チャンネルマスタ・通知ルール・テスト通知を管理する。
 * @graph-connects ChannelSection [calls] チャンネル管理
 * @graph-connects RuleSection [calls] ルール管理
 * @graph-connects TestNotificationSection [calls] テスト通知
 */
export function NotificationPage() {
  const { t } = useTranslation();
  const channelsQuery = useNotificationChannels();
  const channels = channelsQuery.data?.channels ?? [];

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6">
      <header className="flex items-center gap-3">
        <Bell className="h-6 w-6 text-[var(--color-accent-primary)]" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
            {t('notification.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {t('notification.description')}
          </p>
        </div>
      </header>

      <ChannelSection canEdit />
      <RuleSection channels={channels} canEdit />
      <TestNotificationSection />
    </div>
  );
}
